/**
 * Consolidated data transformation utilities for Analytics components
 * Combines chart data generation and pivot transformations with optimized performance
 */
import { MssGridData, MssPivotGridData, RoyaltiesGridData } from 'src/hooks/analytics';

import { MAJOR_LABELS, VENDOR_PATTERNS, type VendorCategory } from './constants';

// Re-export types for convenience
export type { VendorCategory } from './constants';

// Chart data interfaces
export interface ChartDataPoint {
    month: string;
    UMG?: number;
    SONY?: number;
    WMG?: number;
    Indies?: number;
    'UMG Music-Only'?: number;
    [key: string]: string | number | undefined;
}

export interface AggregatedVendorData {
    month: string;
    territory?: string;
    UMG: number;
    SONY: number;
    WMG: number;
    Indies: number;
    'UMG Music-Only': number;
}

// Pivot data interfaces
export interface RoyaltiesPivotData {
    territory: string;
    vendor: string;
    [monthKey: string]: string | number;
}

export interface MssPivotData {
    territory: string;
    vendor: string;
    [monthKey: string]: string | number;
}

// Validation result interface
export interface ValidationResult {
    isValid: boolean;
    issues: string[];
    monthsWithData: string[];
    monthsWithIssues: string[];
}

/**
 * Utility function to identify UMG & affiliate vendors
 */
const isUmgAffiliate = (vendor: string): boolean => {
    if (!vendor) return false;
    const vendorLower = vendor.toLowerCase();
    return vendorLower.includes('universal') && vendorLower.includes('affiliate');
};

/**
 * Classifies a vendor name into major label or Indies category
 * Optimized with memoization for better performance
 */
const vendorClassificationCache = new Map<string, VendorCategory>();

export function classifyVendor(entityName: string): VendorCategory {
    if (!entityName) return 'Indies';

    // Check cache first
    if (vendorClassificationCache.has(entityName)) {
        return vendorClassificationCache.get(entityName)!;
    }

    const normalizedName = entityName.toLowerCase();
    let category: VendorCategory = 'Indies';

    // Check each major label pattern
    for (const [label, patterns] of Object.entries(VENDOR_PATTERNS)) {
        if (patterns.some((pattern: string) => normalizedName.includes(pattern))) {
            category = label as VendorCategory;
            break;
        }
    }

    // Cache the result
    vendorClassificationCache.set(entityName, category);
    return category;
}

/**
 * Validates and sanitizes input data
 */
function validateRoyaltiesData(data: RoyaltiesGridData[]): RoyaltiesGridData[] {
    return data.filter(
        (item) =>
            item &&
            typeof item.month === 'string' &&
            typeof item.territory === 'string' &&
            typeof item.vendor === 'string' &&
            typeof item.earning === 'number' &&
            !isNaN(item.earning) &&
            item.earning >= 0
    );
}

function validateMssData(data: MssGridData[]): MssGridData[] {
    const validData = data.filter(
        (item) =>
            item &&
            typeof item.month === 'string' &&
            typeof item.territory === 'string' &&
            typeof item.vendor === 'string' &&
            typeof item.vendorPlays === 'number' &&
            typeof item.totalPlays === 'number' &&
            !isNaN(item.vendorPlays) &&
            !isNaN(item.totalPlays) &&
            item.vendorPlays >= 0 &&
            item.totalPlays > 0
    );

    const invalidCount = data.length - validData.length;
    if (invalidCount > 0) {
        console.warn(`MSS data validation: filtered out ${invalidCount} invalid records out of ${data.length} total`);
    }

    return validData;
}

/**
 * Aggregates royalties data by major labels and Indies
 * Enhanced with validation and performance optimizations
 */
export function aggregateRoyaltiesByMajorLabels(
    data: RoyaltiesGridData[],
    filteredTerritories?: string[],
    filteredVendors?: string[]
): AggregatedVendorData[] {
    if (!data || data.length === 0) {
        return [];
    }

    // Validate and filter data
    const validData = validateRoyaltiesData(data);
    if (validData.length === 0) {
        return [];
    }

    // Apply territory filter if specified
    let filteredData =
        filteredTerritories && filteredTerritories.length > 0
            ? validData.filter((item) => filteredTerritories.includes(item.territory))
            : validData;

    // Apply vendor filter if specified
    if (filteredVendors && filteredVendors.length > 0) {
        filteredData = filteredData.filter((item) => filteredVendors.includes(item.vendor));
    }

    // Group by month using Map for better performance
    const monthGroups = new Map<string, RoyaltiesGridData[]>();
    filteredData.forEach((item) => {
        if (!monthGroups.has(item.month)) {
            monthGroups.set(item.month, []);
        }
        monthGroups.get(item.month)!.push(item);
    });

    // Aggregate by vendor categories for each month
    const result = Array.from(monthGroups.entries()).map(([month, monthData]) => {
        const aggregated: AggregatedVendorData = {
            month,
            UMG: 0,
            SONY: 0,
            WMG: 0,
            Indies: 0,
            'UMG Music-Only': 0
        };

        monthData.forEach((item) => {
            const category = classifyVendor(item.vendor);
            aggregated[category] += item.earningInUSD;
        });

        return aggregated;
    });

    return result.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * Aggregates MSS data by major labels and Indies
 * Enhanced with validation and anti-dilution mode support
 */
export function aggregateMssByMajorLabels(
    data: MssGridData[],
    filteredTerritories?: string[],
    filteredVendors?: string[]
): AggregatedVendorData[] {
    if (!data || data.length === 0) {
        return [];
    }

    // Validate and filter data
    const validData = validateMssData(data);
    if (validData.length === 0) {
        return [];
    }

    // Apply territory filter if specified
    let filteredData =
        filteredTerritories && filteredTerritories.length > 0
            ? validData.filter((item) => filteredTerritories.includes(item.territory))
            : validData;

    // Apply vendor filter if specified
    if (filteredVendors && filteredVendors.length > 0) {
        filteredData = filteredData.filter((item) => filteredVendors.includes(item.vendor));
    }

    // Group by month using Map for better performance
    const monthGroups = new Map<string, MssGridData[]>();
    filteredData.forEach((item) => {
        if (!monthGroups.has(item.month)) {
            monthGroups.set(item.month, []);
        }
        monthGroups.get(item.month)?.push(item);
    });

    // Calculate MSS percentages for each month
    const result = Array.from(monthGroups.entries()).map(([month, monthData]) => {
        // Aggregate vendor plays by category (excluding UMG Music-Only as it's calculated separately)
        const vendorPlays: Record<Exclude<VendorCategory, 'UMG Music-Only'>, number> = {
            UMG: 0,
            SONY: 0,
            WMG: 0,
            Indies: 0
        };

        // Sum vendor plays across all rows
        monthData.forEach((item) => {
            const category = classifyVendor(item.vendor);
            // Always use regular vendor plays for main categories (excluding UMG Music-Only)
            if (category !== 'UMG Music-Only') {
                vendorPlays[category] += item.vendorPlays;
            }
        });

        // Calculate total plays sum by territory (avoiding double-counting)
        const territoryTotalPlays = new Map<string, number>();

        monthData.forEach((item) => {
            // Always use regular total plays for main calculation
            territoryTotalPlays.set(item.territory, item.totalPlays);
        });

        // Sum total plays across all territories
        const totalPlaysSum = Array.from(territoryTotalPlays.values()).reduce((sum, plays) => sum + plays, 0);

        // Calculate UMG Music-Only MSS using the same approach as other labels
        let umgMusicOnlyVendorPlays = 0;
        let umgMusicOnlyTotalPlays = 0;

        // Aggregate music-only plays for UMG vendors
        monthData.forEach((item) => {
            const category = classifyVendor(item.vendor);
            if (category === 'UMG' && isUmgAffiliate(item.vendor)) {
                umgMusicOnlyVendorPlays += item.musicOnlyVendorPlays || 0;
                umgMusicOnlyTotalPlays += item.musicOnlyTotalPlays || 0;
            }
        });

        // Calculate UMG Music-Only MSS percentage
        const umgMusicOnlyMss =
            umgMusicOnlyTotalPlays > 0 ? (umgMusicOnlyVendorPlays / umgMusicOnlyTotalPlays) * 100 : 0;

        // Calculate MSS percentages
        const aggregated: AggregatedVendorData = {
            month,
            UMG: totalPlaysSum > 0 ? (vendorPlays.UMG / totalPlaysSum) * 100 : 0,
            SONY: totalPlaysSum > 0 ? (vendorPlays.SONY / totalPlaysSum) * 100 : 0,
            WMG: totalPlaysSum > 0 ? (vendorPlays.WMG / totalPlaysSum) * 100 : 0,
            Indies: totalPlaysSum > 0 ? (vendorPlays.Indies / totalPlaysSum) * 100 : 0,
            'UMG Music-Only': umgMusicOnlyMss
        };

        return aggregated;
    });

    return result.sort((a, b) => a.month.localeCompare(b.month));
}

/**
 * Transforms flat royalties data into pivot format with months as columns
 */
export function transformRoyaltiesToPivot(data: RoyaltiesGridData[]): RoyaltiesPivotData[] {
    if (!data || data.length === 0) return [];

    const validData = validateRoyaltiesData(data);
    if (validData.length === 0) return [];

    // Group by territory + vendor combination using Map for better performance
    const grouped = new Map<string, RoyaltiesPivotData>();

    validData.forEach((item) => {
        const key = `${item.territory}-${item.vendor}`;

        if (!grouped.has(key)) {
            grouped.set(key, {
                territory: item.territory,
                vendor: item.vendor
            });
        }

        // Add month data with earnings value
        grouped.get(key)![item.month] = item.earningInUSD ?? 0;
    });

    // Convert to array and sort
    return Array.from(grouped.values()).sort((a, b) => {
        if (a.territory !== b.territory) {
            return a.territory.localeCompare(b.territory);
        }
        return a.vendor.localeCompare(b.vendor);
    });
}

/**
 * Transforms flat MSS data into pivot format with months as column groups
 */
export function transformMssToPivot(data: MssGridData[]): MssPivotData[] {
    if (!data || data.length === 0) return [];

    const validData = validateMssData(data);
    if (validData.length === 0) return [];

    // Group by territory + vendor combination using Map for better performance
    const grouped = new Map<string, MssPivotData>();

    validData.forEach((item) => {
        const key = `${item.territory}-${item.vendor}`;

        if (!grouped.has(key)) {
            grouped.set(key, {
                territory: item.territory,
                vendor: item.vendor
            });
        }

        const row = grouped.get(key)!;
        const monthPrefix = item.month;
        const isUmgVendor = isUmgAffiliate(item.vendor);

        // Add month data with 3 sub-columns per month
        row[`${monthPrefix}_vendorPlays`] = item.vendorPlays ?? 0;
        row[`${monthPrefix}_totalPlays`] = item.totalPlays ?? 0;
        row[`${monthPrefix}_mss`] = item.mss ?? 0;

        // Add music-only fields for UMG & affiliate vendors
        if (isUmgVendor) {
            row[`${monthPrefix}_musicOnlyVendorPlays`] = item.musicOnlyVendorPlays ?? 0;
            row[`${monthPrefix}_musicOnlyTotalPlays`] = item.musicOnlyTotalPlays ?? 0;
            row[`${monthPrefix}_musicOnlyMss`] = item.musicOnlyMss ?? 0;
        }
    });

    // Convert to array and sort
    return Array.from(grouped.values()).sort((a, b) => {
        if (a.territory !== b.territory) {
            return a.territory.localeCompare(b.territory);
        }
        return a.vendor.localeCompare(b.vendor);
    });
}

/**
 * Transforms raw MSS grid data to pivot grid format for use in MssViewPivot
 * This allows us to use a single API call and transform the data as needed
 */
export function transformMssGridToPivotGrid(data: MssGridData[]): MssPivotGridData[] {
    if (!data || data.length === 0) return [];

    // Group by month + territory + vendor combination
    const groups = new Map<string, MssGridData[]>();

    data.forEach((item) => {
        const key = `${item.month}-${item.territory}-${item.vendor}`;
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key)?.push(item);
    });

    // Transform each group into MssPivotGridData format
    const pivotData: MssPivotGridData[] = [];

    groups.forEach((groupItems) => {
        if (groupItems.length === 0) return;

        const firstItem = groupItems[0];

        // Find ACTM and Standard records
        const actmRecord = groupItems.find((item) => item.isArtistCentric === true);
        const standardRecord = groupItems.find((item) => item.isArtistCentric === false);

        // Determine which mode(s) are eligible for earnings submission
        const actmEligible = actmRecord?.eligibleForEarningsSubmission === true;
        const standardEligible = standardRecord?.eligibleForEarningsSubmission === true;

        let eligibleMode: 'actm' | 'standard' | 'none';
        if (actmEligible) {
            eligibleMode = 'actm';
        } else if (standardEligible) {
            eligibleMode = 'standard';
        } else {
            eligibleMode = 'none';
        }

        const pivotEntry: MssPivotGridData = {
            month: firstItem.month,
            territory: firstItem.territory,
            vendor: firstItem.vendor,
            eligibleMode
        };

        // Populate ACTM fields if ACTM record exists
        if (actmRecord) {
            pivotEntry.actmVendorPlays = actmRecord.vendorPlays;
            pivotEntry.actmTotalPlays = actmRecord.totalPlays;
            pivotEntry.actmMss = actmRecord.mss;
            pivotEntry.actmMusicOnlyVendorPlays = actmRecord.musicOnlyVendorPlays;
            pivotEntry.actmMusicOnlyTotalPlays = actmRecord.musicOnlyTotalPlays;
            // Calculate music-only MSS if data is available
            if (
                actmRecord.musicOnlyVendorPlays &&
                actmRecord.musicOnlyTotalPlays &&
                actmRecord.musicOnlyTotalPlays > 0
            ) {
                pivotEntry.actmMusicOnlyMss = actmRecord.musicOnlyVendorPlays / actmRecord.musicOnlyTotalPlays;
            }
        }

        // Populate Standard fields if Standard record exists
        if (standardRecord) {
            pivotEntry.standardVendorPlays = standardRecord.vendorPlays;
            pivotEntry.standardTotalPlays = standardRecord.totalPlays;
            pivotEntry.standardMss = standardRecord.mss;
            pivotEntry.standardMusicOnlyVendorPlays = standardRecord.musicOnlyVendorPlays;
            pivotEntry.standardMusicOnlyTotalPlays = standardRecord.musicOnlyTotalPlays;
            // Calculate music-only MSS if data is available
            if (
                standardRecord.musicOnlyVendorPlays &&
                standardRecord.musicOnlyTotalPlays &&
                standardRecord.musicOnlyTotalPlays > 0
            ) {
                pivotEntry.standardMusicOnlyMss =
                    standardRecord.musicOnlyVendorPlays / standardRecord.musicOnlyTotalPlays;
            }
        }

        pivotData.push(pivotEntry);
    });

    return pivotData;
}

/**
 * Transforms MSS pivot grid data into pivot format with 6 columns per month (ACTM + Standard)
 */
export function transformMssPivotGridToPivot(data: MssPivotGridData[]): MssPivotData[] {
    if (!data || data.length === 0) return [];

    // Group by territory + vendor combination using Map for better performance
    const grouped = new Map<string, MssPivotData>();

    data.forEach((item) => {
        const key = `${item.territory}-${item.vendor}`;

        if (!grouped.has(key)) {
            grouped.set(key, {
                territory: item.territory,
                vendor: item.vendor
            });
        }

        const row = grouped.get(key)!;
        const monthPrefix = item.month;

        // Add ACTM columns (isArtistCentric=true data)
        row[`${monthPrefix}_actmVendorPlays`] = item.actmVendorPlays ?? 0;
        row[`${monthPrefix}_actmTotalPlays`] = item.actmTotalPlays ?? 0;
        row[`${monthPrefix}_actmMss`] = item.actmMss ?? 0;

        // Add Standard columns (isArtistCentric=false data)
        row[`${monthPrefix}_standardVendorPlays`] = item.standardVendorPlays ?? 0;
        row[`${monthPrefix}_standardTotalPlays`] = item.standardTotalPlays ?? 0;
        row[`${monthPrefix}_standardMss`] = item.standardMss ?? 0;

        row[`${monthPrefix}_eligibleMode`] = item.eligibleMode ?? 'none';

        // Add music-only fields for UMG & affiliate vendors
        const isUmgVendor = isUmgAffiliate(item.vendor);
        if (isUmgVendor) {
            // ACTM Music-Only
            row[`${monthPrefix}_actmMusicOnlyVendorPlays`] = item.actmMusicOnlyVendorPlays ?? 0;
            row[`${monthPrefix}_actmMusicOnlyTotalPlays`] = item.actmMusicOnlyTotalPlays ?? 0;
            row[`${monthPrefix}_actmMusicOnlyMss`] = item.actmMusicOnlyMss ?? 0;

            // Standard Music-Only
            row[`${monthPrefix}_standardMusicOnlyVendorPlays`] = item.standardMusicOnlyVendorPlays ?? 0;
            row[`${monthPrefix}_standardMusicOnlyTotalPlays`] = item.standardMusicOnlyTotalPlays ?? 0;
            row[`${monthPrefix}_standardMusicOnlyMss`] = item.standardMusicOnlyMss ?? 0;
        }
    });

    // Convert to array and sort
    return Array.from(grouped.values()).sort((a, b) => {
        if (a.territory !== b.territory) {
            return a.territory.localeCompare(b.territory);
        }
        return a.vendor.localeCompare(b.vendor);
    });
}

/**
 * Gets unique months from data arrays
 */
export function getUniqueMonths(data: (RoyaltiesGridData | MssGridData | MssPivotGridData)[]): string[] {
    if (!data || data.length === 0) return [];

    const months = new Set(data.map((item) => item.month).filter((month) => month != null));
    return Array.from(months).sort();
}

/**
 * Normalizes chart data to 100% for stacked bar charts
 */
export function normalizeChartDataToPercent(data: ChartDataPoint[]): ChartDataPoint[] {
    return data.map((point) => {
        const total = (point.UMG || 0) + (point.SONY || 0) + (point.WMG || 0) + (point.Indies || 0);

        if (total === 0) {
            return { ...point };
        }

        return {
            ...point,
            UMG: point.UMG ? (point.UMG / total) * 100 : 0,
            SONY: point.SONY ? (point.SONY / total) * 100 : 0,
            WMG: point.WMG ? (point.WMG / total) * 100 : 0,
            Indies: point.Indies ? (point.Indies / total) * 100 : 0
        };
    });
}

/**
 * Filters chart data based on selected vendors
 */
export function filterChartDataByVendors(
    data: ChartDataPoint[],
    selectedVendors: string[]
): { filteredData: ChartDataPoint[]; availableVendors: VendorCategory[] } {
    if (!data || data.length === 0) {
        return {
            filteredData: [],
            availableVendors: []
        };
    }

    // Get all available vendors from the data
    const availableVendors = getAvailableVendors(data);

    // If no vendors selected, return all data
    if (!selectedVendors || selectedVendors.length === 0) {
        return {
            filteredData: data,
            availableVendors
        };
    }

    // Filter data to only include selected vendors
    const filteredData = data.map((dataPoint) => {
        const filtered: ChartDataPoint = { month: dataPoint.month };

        selectedVendors.forEach((vendor) => {
            if (availableVendors.includes(vendor as VendorCategory)) {
                filtered[vendor] = dataPoint[vendor];
            }
        });

        return filtered;
    });

    // Return only vendors that are both available and selected
    const filteredAvailableVendors = availableVendors.filter((vendor) => selectedVendors.includes(vendor));

    return {
        filteredData,
        availableVendors: filteredAvailableVendors
    };
}

/**
 * Gets available vendor categories from chart data
 */
export function getAvailableVendors(data: ChartDataPoint[]): VendorCategory[] {
    const vendors = new Set<VendorCategory>();

    data.forEach((point) => {
        MAJOR_LABELS.forEach((label) => {
            if ((point[label] || 0) > 0) vendors.add(label);
        });
        if ((point.Indies || 0) > 0) vendors.add('Indies');
        if ((point['UMG Music-Only'] || 0) > 0) vendors.add('UMG Music-Only');
    });

    return Array.from(vendors);
}

/**
 * Validates chart data for completeness and consistency
 */
export function validateChartData(data: ChartDataPoint[]): ValidationResult {
    const issues: string[] = [];
    const monthsWithData = new Set<string>();
    const monthsWithIssues = new Set<string>();

    if (!data || data.length === 0) {
        return {
            isValid: false,
            issues: ['No data provided'],
            monthsWithData: [],
            monthsWithIssues: []
        };
    }

    data.forEach((point, index) => {
        if (!point.month) {
            issues.push(`Data point ${index} missing month`);
            monthsWithIssues.add(`point-${index}`);
            return;
        }

        monthsWithData.add(point.month);

        const total = (point.UMG || 0) + (point.SONY || 0) + (point.WMG || 0) + (point.Indies || 0);

        if (total === 0) {
            issues.push(`Month ${point.month} has no data for any vendor`);
            monthsWithIssues.add(point.month);
        }

        // Check for negative values
        [...MAJOR_LABELS, 'Indies'].forEach((vendor) => {
            const value = point[vendor as keyof ChartDataPoint] as number;
            if (value && value < 0) {
                issues.push(`Month ${point.month} has negative value for ${vendor}: ${value}`);
                monthsWithIssues.add(point.month);
            }
        });
    });

    return {
        isValid: issues.length === 0,
        issues,
        monthsWithData: Array.from(monthsWithData).sort(),
        monthsWithIssues: Array.from(monthsWithIssues).sort()
    };
}

/**
 * Calculates data completeness percentage for progressive loading
 */
export function calculateDataCompleteness(
    currentData: ChartDataPoint[],
    expectedMonths: string[]
): {
    completionPercentage: number;
    loadedMonths: string[];
    missingMonths: string[];
} {
    const validation = validateChartData(currentData);
    const loadedMonths = validation.monthsWithData;
    const missingMonths = expectedMonths.filter((month) => !loadedMonths.includes(month));

    const completionPercentage =
        expectedMonths.length > 0 ? Math.round((loadedMonths.length / expectedMonths.length) * 100) : 0;

    return {
        completionPercentage,
        loadedMonths,
        missingMonths
    };
}
